---
title: Y tho? Warum es jetzt Zeit für neue Management-Methoden ist
date: '2018-05-10'
excerpt: >-
  Warum innovative Management-Methoden heute relevanter sind denn je. Ein
  Schlaglicht auf die Veränderungen in Unternehmen, den Faktor Mensch und die
  Herausforderungen moderner Führung.
metaTitle: Neue Management-Methoden – Warum heute Umdenken gefragt ist
metaDescription: >-
  Erfahren Sie, warum moderne Unternehmen und Manager neue Wege gehen müssen, um
  im globalen Wettbewerb zu bestehen – mit Fokus auf Mensch, Generation Y und
  agile Methoden.
language: de
slug: y-tho-warum-es-zeit-fuer-neue-management-methoden-ist
author: Kevin Rassner
---

Ohne große Umschweife möchte ich ein kurzes Schlaglicht auf einen Aspekt legen, welcher zeigt, warum die Inhalte dieses Blogs relevanter denn je sind und welche Fragestellungen sich für moderne Manager ergeben.

## Die Welt ändert sich

Man könnte meinen, dass für moderne Unternehmen der Faktor Mensch durch die wachsende Intelligenz der Computer an Wettbewerbsrelevanz verliert. Doch lässt sich der Erfolg von Apple, Google, Airbnb oder Uber durch den geschickten Einsatz von Technologie erklären?

Die sogenannte Taylorwanne zeigt diesen Irrglauben eindrücklich auf:

Die Taylorwanne erklärt, wie die Globalisierung Methoden und Technologie zunächst als Wettbewerbsvorteil etablierte, um Marktlücken zu füllen. Durch die Erschließung dieser Märkte verlieren kausale Anteile jedoch wieder an Relevanz. Die wahre Differenzierung erfolgt zunehmend durch den Menschen.

Vor der Erfindung des Fließbandes war der Unternehmer erfolgreich, der sein Handwerk am meisten beherrschte, tolle Ideen hatte und flexibel auf Änderungen im Markt reagieren konnte. Diese Manufakturbetriebe waren meist lokal geschützt. Ein Schmied aus dem Neckartal musste keinen Schmied im Rheintal fürchten. Selbst bei sehr guten Produkten beschränkte die handwerkliche Komponente den Marktzugang.

Mit der Einführung der Fließbandproduktion 1910 endete das Manufakturzeitalter. Plötzlich waren Firmen erfolgreich, die Kosten senkten und Prozesse automatisierten. Der Mensch geriet im Taylorismus oft unter die Räder.

Die Krux bei Advokaten von modernen Managementtheorien ist, dass viele Entscheidungsträger den Wandel noch nicht verstanden haben. Effizienzdruck weicht Innovationsdruck. Kein Zufall, dass die mittlere Anzahl der Automobilhersteller wieder zunimmt! Technik ist kopierbar geworden, wie die [Trust Economy](/blog/die-trust-economy) zeigt. Differenzierung geschieht immer mehr über den Menschen.

Das Modell widerspricht der Annahme, dass die technologische Front am wichtigsten sei. Technologien konzentrieren sich mehr und mehr auf den Menschen: Kollaboration, Kommunikation und Wissensmanagement sind die Verkaufsschlager.

## Die Menschen ändern sich: Generation Y

Nicht nur die wirtschaftlichen Rahmenbedingungen, auch die Menschen selbst ändern sich. Über die Generation Y kursieren viele widersprüchliche und oft falsche Behauptungen. Oft werden Ursache und Wirkung verwechselt - das schwindende Interesse an Eigenheimen hängt mit wachsenden Kosten ebenso wie mit neuen Arbeits- und Lebensmodellen zusammen.

Meine Einschätzung dieser Generation (zu der ich gehöre): Selbstverwirklichung, Zugehörigkeit, Sinnhaftigkeit, Spaß und Familie haben einen hohen Stellenwert. Die Netzwerkökonomie bietet genau diese Werte. Kreativität verlangt Freiräume; Zugehörigkeit braucht Vertrauen; Sinnhaftigkeit bedingt Einblicke in die Wirkung des eigenen Handelns.

## Die Arbeitswelt muss sich auch ändern!

Die Umwelt erfordert und die Menschen wollen neue Bedingungen. Doch viele Firmen und Führungskräfte sind noch nicht soweit.

Douglas McGregor (MIT Sloan School of Management) beschrieb diese Entwicklung schon 1960: Theory X (alte Welt) und Theory Y (neue ideale Welt). Doch vielerorts herrscht immer noch Theory X.

Vor allem in der Softwareentwicklung sieht man seit Jahren eine Y-Welt: Agile Methoden wie Scrum setzen auf Teilhabe und Dezentralisierung.

Microsoft probierte in [42projects](https://www.42projects.org/) moderne Methoden aus.

Nicht nur Tätigkeiten, auch Wissen und Sinn werden dezentralisiert. Simon Sinek erklärt in [Start with why](https://www.amazon.de/Start-Why-Leaders-Inspire-Everyone/dp/1591846447):  
> People don't buy what you do, they buy why you do it.

Apple ist deshalb so erfolgreich, weil es „Ys“ (Generation und Theorie) und das Warum so groß schreibt.

McGregor erkannte, dass auch TheoryX-Firmen erfolgreich sind. Mein Anspruch an modernes Management: Menschzentrierung, nicht nur Profit. Ziel ist das Wohlergehen aller Stakeholder.

## Fragen, die sich stellen:

1. Wie schafft man es, Menschen zu befähigen, ihr volles Entwicklungspotential auszuschöpfen?
2. Wie können Unternehmen Herausforderungen der Zukunft erkennen und reagieren?
3. Wie organisiert man Unternehmen so, dass diese Rahmenbedingungen gegeben sind?

Nach Kenneth Murrell werden alle Artikel mindestens einer Kategorie zugeordnet:

1. Sich und andere Menschen verstehen
2. Teams verstehen
3. Organisationen verstehen

Die Kategorien bauen aufeinander auf. Erst wer sich selbst versteht, kann andere verstehen. Wer andere versteht, versteht Teams. Wer Teams versteht, erkennt Organisationsprozesse - und damit die Welt.

---

**Weitere Artikel und Inspirationen finden Sie im EmpowerMensch-Blog.**

